import { LANGUAGES } from 'config/constants/languages'
import useModal from 'hooks/useModal'
import { RowMiddle } from 'layout/Components/Row'
import React, { useMemo, useState } from 'react'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import { ModalBody, ModalHeader } from 'UIKit/AlchemyModal'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import IconButton from 'UIKit/Button/IconButton'
import { ModalProps } from 'UIKit/Modal'
import Text from 'UIKit/Text'
import { formatDisplayDate, isEqualDate } from 'utils/dateHelper'
import {
  PAYMENT_MODAL_THEME,
  PaymentChevronRightIcon,
  PaymentInputContainer,
  PaymentLabel,
  StyledActionButton,
  StylePaymentModal,
  StyleSmallAction,
} from '../styles'
import { useTransactionFilter } from './hooks'

interface DateFilterProps {
  fromDate: number
  toDate: number
  onSelect: (fromDate: number, toDate: number) => void

  maxDate?: number
  minDate?: number
}
const ALL_DAY = 0
const PaymentHistoryFilterModal: React.FC<ModalProps<DateFilterProps>> = ({
  data: { onSelect, fromDate, toDate, minDate = undefined, maxDate = undefined },
  onDismiss,
}) => {
  const { t } = useTranslation()
  const { i18n } = useTranslation()
  const [calendarTrigger, setCalendarTrigger] = useState<number>(0)
  const [selectedDaysAgo, setDaysAgo] = useState<number>(fromDate > 0 ? undefined : ALL_DAY)
  const control = useTransactionFilter<Pick<DateFilterProps, 'fromDate' | 'toDate'>>({
    initialData: {
      fromDate,
      toDate,
    },
    onSubmit: (data) => {
      onSelect(data.fromDate, data.toDate)
      onDismiss()
    },
  })

  const selectedLanguage = useMemo(() => LANGUAGES.find((item) => item.code === i18n.language), [i18n.language])

  const handleDateRange = (selectedDateRange: number[] | Date[]) => {
    const selected = Array.isArray(selectedDateRange)
      ? selectedDateRange.map((s) => (typeof s === 'number' ? s : s && s.getTime()))
      : [selectedDateRange]

    let selectedFrom = selected?.[0] || null
    let selectedTo = selected?.[1] || null

    if (minDate > 0 && selectedFrom < minDate) {
      selectedFrom = minDate
    }
    if (maxDate > 0 && selectedTo > maxDate) {
      selectedTo = maxDate
    }
    control.onChange('fromDate', selectedFrom)
    control.onChange('toDate', selectedTo || selectedFrom)
    setDaysAgo(undefined)
  }

  return (
    <StylePaymentModal
      minWidth={['100%', '', '', 'auto !important']}
      colorTheme={PAYMENT_MODAL_THEME}
      onDismiss={onDismiss}
    >
      <ModalHeader colorTheme={PAYMENT_MODAL_THEME} title={t('')} />
      <ModalBody gap="var(--pm-space-component, 20px)">
        <StyledWrapCalendar>
          <Calendar
            key={calendarTrigger}
            onChange={handleDateRange}
            value={[
              control.filters.fromDate ? new Date(control.filters.fromDate) : undefined,
              control.filters.fromDate ? new Date(control.filters.toDate) : undefined,
            ]}
            locale={selectedLanguage.IETFCode}
            nextLabel={<Icons.ChevronRightIcon fill={theme.colors.text} width="16px" height="16px" />}
            prevLabel={<Icons.ChevronLeftIcon fill={theme.colors.text} width="16px" height="16px" />}
            prev2Label={null}
            next2Label={null}
            maxDate={maxDate}
            minDate={minDate}
            selectRange
            allowPartialRange
          />
        </StyledWrapCalendar>

        <Flex justifyContent="center" gap="8px" flexWrap="wrap">
          {[
            {
              title: t('All'),
              code: ALL_DAY,
            },
            {
              title: t('{{value}} week', { value: 1 }),
              code: 7,
            },
            {
              title: t('{{value}} month', { value: 1 }),
              code: 31,
            },
            {
              title: t('{{value}} months', { value: 3 }),
              code: 93,
            },
          ].map((item) => (
            <StyleSmallAction
              style={{
                textTransform: 'capitalize',
              }}
              active={item.code === selectedDaysAgo}
              onClick={() => {
                if (item.code === ALL_DAY) {
                  handleDateRange(null)
                } else {
                  handleDateRange(daysAgo(Date.now(), item.code))
                }
                setCalendarTrigger((pre) => pre + 1)
                setDaysAgo(item.code)
              }}
            >
              {item.title}
            </StyleSmallAction>
          ))}
        </Flex>

        <StyledActionButton
          mx="auto"
          width="100%"
          maxWidth="324px"
          variant={control.isInvalid ? 'secondary' : 'primary'}
          onClick={control.submit}
        >
          <Trans>Apply</Trans>
        </StyledActionButton>
      </ModalBody>
    </StylePaymentModal>
  )
}

function daysAgo(date: number, daysAgo: number) {
  const baseDate = new Date(date)

  baseDate.setDate(baseDate.getDate() - daysAgo)

  const fromDate = new Date(baseDate)
  fromDate.setHours(0, 0, 0, 0)

  const toDate = new Date(date)
  toDate.setHours(23, 59, 59, 999)

  return [fromDate.getTime(), toDate.getTime()]
}

export const SelectedDateItem: React.FC<DateFilterProps> = ({ fromDate, toDate, onSelect, ...props }) => {
  const [onPresent] = useModal(PaymentHistoryFilterModal)

  return (
    <PaymentInputContainer
      justifyContent="space-between"
      p="12px"
      onClick={() =>
        onPresent({
          fromDate,
          toDate,
          onSelect,
          ...props,
        })
      }
    >
      <PaymentLabel color="var(--pm-text-trigger-color)">
        <Trans>Date</Trans>
      </PaymentLabel>

      {fromDate > 0 ? (
        <RowMiddle gap="8px">
          {isEqualDate(new Date(fromDate), new Date(toDate)) ? (
            <StyledText>{formatDisplayDate(new Date(fromDate))}</StyledText>
          ) : (
            <RowMiddle gap="4px">
              <StyledText>{formatDisplayDate(new Date(fromDate))}</StyledText>
              <StyledText>-</StyledText>
              <StyledText>{formatDisplayDate(new Date(toDate))}</StyledText>
            </RowMiddle>
          )}
          <IconButton width={12}>
            <Icons.ChevronRightIcon width="8px" />
          </IconButton>
        </RowMiddle>
      ) : (
        <RowMiddle gap="8px">
          <StyledText>
            <Trans>{'All Time'}</Trans>
          </StyledText>
          <RowMiddle width={12}>
            <PaymentChevronRightIcon />
          </RowMiddle>
        </RowMiddle>
      )}
    </PaymentInputContainer>
  )
}

const StyledText = styled(Text)`
  color: var(--pm-label-color, ${(props) => props.theme.colors.text_tertiary});
  font-size: 14px;
  font-weight: 400;
`

const StyledWrapCalendar = styled(Box)`
  width: 100%;

  abbr[title] {
    text-decoration: unset;
    color: ${(props) => props.theme.colors.textSubtle};
  }

  .react-calendar__navigation button {
    background: transparent !important;
  }

  .react-calendar__navigation button[disabled],
  button[disabled] {
    background-color: transparent;
    opacity: 0.3;
  }
  .react-calendar__tile:enabled:hover {
    background: unset !important;
  }

  .react-calendar__tile--hasActive.react-calendar__tile--rangeStart,
  .react-calendar__tile--active.react-calendar__tile--rangeStart,
  .react-calendar--selectRange .react-calendar__tile--hover.react-calendar__tile--hoverStart {
    position: relative;
    background: unset !important;

    &:before {
      left: 10px;
      border-top-left-radius: 50%;
      border-bottom-left-radius: 50%;

      right: unset;
    }
  }

  .react-calendar__tile--hasActive.react-calendar__tile--rangeEnd,
  .react-calendar__tile--active.react-calendar__tile--rangeEnd,
  .react-calendar--selectRange .react-calendar__tile--hover.react-calendar__tile--hoverEnd {
    background: unset !important;
    &:before {
      left: unset;

      right: 10px;
      border-top-right-radius: 50%;
      border-bottom-right-radius: 50%;
    }
  }

  .react-calendar__tile--rangeBothEnds.react-calendar__tile--hover.react-calendar__tile--hoverStart.react-calendar__tile--hoverEnd.react-calendar__tile--hoverBothEnds,
  .react-calendar__tile--rangeBothEnds:not(.react-calendar__tile--hover),
  .react-calendar__tile--hoverBothEnds:not(.react-calendar__tile--hover) {
    &:before {
      width: 0 !important;
      border-radius: 50% !important;
    }
  }

  .react-calendar__tile--hasActive.react-calendar__tile--rangeStart,
  .react-calendar__tile--hasActive.react-calendar__tile--rangeEnd,
  .react-calendar--selectRange .react-calendar__tile--hover.react-calendar__tile--hoverEnd,
  .react-calendar--selectRange .react-calendar__tile--hover.react-calendar__tile--hoverStart,
  .react-calendar__tile--active.react-calendar__tile--rangeStart,
  .react-calendar__tile--active.react-calendar__tile--rangeEnd {
    background: unset !important;
    position: relative;

    &:not(.react-calendar__year-view__months__month) {
      background: unset !important;
      position: relative;
      abbr {
        white-space: nowrap;
        background: ${(props) => props.theme.colors.strokeAlt3};
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        padding: 2.5px;
      }
    }
  }

  .react-calendar__tile--hasActive,
  .react-calendar__tile--active,
  .react-calendar--selectRange .react-calendar__tile--hover {
    border-radius: unset !important;
    background: unset !important;

    &:not(.react-calendar__year-view__months__month):not(.react-calendar__decade-view__years__year):not(
        .react-calendar__century-view__decades__decade
      ) {
      &:before {
        content: '';
        position: absolute;
        background: rgba(24, 119, 242, 0.32);
        width: 46px;
        height: 30px;
      }

      abbr {
        z-index: 1;
      }
    }
  }

  .react-calendar {
    width: 324px;
    border: none;
    background: transparent;
    margin-inline: auto;

    .react-calendar__navigation__label__labelText--from {
      color: ${({ theme: { colors } }) => colors.text};
      font-size: 15px;
      font-weight: bold;
    }

    &__tile {
      &--now {
        background: unset !important;
      }

      ${({ theme }) => theme.mediaQueries.md} {
        &:enabled:hover,
        &:enabled:focus {
          position: relative;
          background: ${({ theme: { colors } }) => colors.strokeBlur};
        }
      }
    }

    .react-calendar__month {
      &-view__weekdays__weekday {
        height: 40px;
        width: 40px;
        max-width: 46px;

        display: flex;
        justify-content: center;
        align-items: center;
        padding: unset;
        overflow: hidden;
      }
      &-view__days__day {
        border-radius: 50px;
        height: 40px;
        width: 40px;
        max-width: 46px;

        display: flex;
        justify-content: center;
        align-items: center;

        &--neighboringMonth {
          color: ${(props) => props.theme.colors.textAlt1};
        }

        ${({ theme }) => theme.mediaQueries.md} {
          &:enabled:hover,
          &:enabled:focus {
            position: relative;
            background: transparent;
            &:after {
              content: ' ';
              position: absolute;
              left: 50%;
              top: 50%;
              transform: translate(-50%, -50%);
              background: ${({ theme: { colors } }) => colors.strokeBlur};
              width: 30px;
              height: 30px;
              border-radius: 50%;
            }
          }
        }
      }
    }

    ${({ theme }) => theme.mediaQueries.md} {
      .react-calendar__navigation {
        button {
          &:enabled:hover,
          &:enabled:focus {
            background: #22262e;
          }
        }
      }
    }

    button {
      border-radius: 8px;
      color: #fff;
    }
  }
`
export default PaymentHistoryFilterModal
